import React from 'react';
import { Helmet } from 'react-helmet-async';

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - NuPitch</title>
        <meta name="description" content="Privacy Policy for NuPitch Incentive Match Maker" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container-responsive py-12">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg border border-gray-200 p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
            
            <div className="prose prose-gray max-w-none">
              <p className="text-sm text-gray-600 mb-6">
                <strong>Last updated:</strong> January 27, 2025
              </p>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
                
                <h3 className="text-lg font-medium text-gray-900 mb-2">Information You Provide</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Project descriptions and scripts (when uploaded)</li>
                  <li>Budget information (when provided)</li>
                  <li>Name and email address (for PDF reports)</li>
                  <li>Newsletter preferences</li>
                </ul>

                <h3 className="text-lg font-medium text-gray-900 mb-2">Information We Collect Automatically</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>IP address (hashed for privacy)</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Usage patterns and interactions</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
                <p className="text-gray-700 mb-4">We use the information we collect to:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Analyze your project and match it with relevant incentives</li>
                  <li>Generate personalized reports and recommendations</li>
                  <li>Send you requested PDF reports via email</li>
                  <li>Improve our service and user experience</li>
                  <li>Send newsletter updates (only if you opt in)</li>
                  <li>Respond to your inquiries and provide support</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Information Sharing and Disclosure</h2>
                <p className="text-gray-700 mb-4">
                  We do not sell, trade, or otherwise transfer your personal information to third parties except as described below:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website and conducting our business</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights, property, or safety</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Retention</h2>
                <p className="text-gray-700 mb-4">
                  <strong>Scripts and Content:</strong> Full scripts are automatically deleted within 48 hours of submission. We retain only sanitized excerpts (first 5,000 characters) for processing purposes.
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>Personal Information:</strong> We retain personal information for as long as necessary to provide our services and comply with legal obligations. You may request deletion of your data at any time.
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>Analysis Results:</strong> Incentive matching results are retained to improve our service but are not linked to personal identifiers.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
                <p className="text-gray-700 mb-4">You have the right to:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Object to processing of your personal information</li>
                  <li>Request data portability</li>
                  <li>Withdraw consent at any time</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  To exercise these rights, please contact us at <a href="mailto:info@nupitchtechnologies.com?subject=NuPitch Incentive Match Maker Privacy Rights" className="text-primary-600 hover:underline">info@nupitchtechnologies.com</a>
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking</h2>
                <p className="text-gray-700 mb-4">
                  We use only essential cookies necessary for the operation of our service. We do not use tracking cookies or third-party analytics that collect personal information.
                </p>
                <p className="text-gray-700 mb-4">
                  You can control cookie settings through your browser preferences.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Third-Party Services</h2>
                <p className="text-gray-700 mb-4">
                  Our service may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these third parties.
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>AI Processing:</strong> We use third-party AI services to analyze scripts and project descriptions. These services process your content but do not retain it beyond the processing session.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">9. International Data Transfers</h2>
                <p className="text-gray-700 mb-4">
                  Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with applicable privacy laws.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
                <p className="text-gray-700 mb-4">
                  Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <p className="text-gray-700">
                  Email: <a href="mailto:info@nupitchtechnologies.com?subject=NuPitch Incentive Match Maker Privacy Policy" className="text-primary-600 hover:underline">info@nupitchtechnologies.com</a><br />
                  Website: <a href="/contact" className="text-primary-600 hover:text-primary-800">Contact Form</a>
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">13. GDPR Compliance</h2>
                <p className="text-gray-700 mb-4">
                  If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR). Our legal basis for processing your personal information includes:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li><strong>Consent:</strong> When you provide consent for specific processing activities</li>
                  <li><strong>Contract:</strong> When processing is necessary to provide our services</li>
                  <li><strong>Legitimate Interest:</strong> When we have a legitimate business interest that doesn't override your privacy rights</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;